'use client'

// Problem section — Chapter 01 · The Wound
// Pass 3: pinned cinematic scene. A tall scroll track holds a sticky 100vh
// viewport; five stages cross-fade as the user scrolls, text scrubs in and
// out, numerals count up with scroll, and a darkening overlay deepens the
// backdrop over the whole chapter. See useProblemScene for the timeline.

import { useRef } from 'react'
import { useTranslations } from 'next-intl'
import { useProblemScene } from '@/lib/animations/useProblemScene'

// ─── Shared style tokens (dark-interlude palette) ─────────────────────────────
const CREAM       = '#F4ECD3'
const CREAM_HIGH  = 'rgba(244,236,211,0.96)'
const CREAM_MID   = 'rgba(244,236,211,0.78)'
const CREAM_LOW   = 'rgba(244,236,211,0.52)'
const CREAM_FAINT = 'rgba(244,236,211,0.32)'
const LIME        = 'var(--lime-500)'
const FONT_BODY   = 'var(--font-body)'
const FONT_SERIF  = 'var(--font-serif)'
const FONT_DISP   = 'var(--font-display)'

// ─── Typography primitives (reused across stages) ─────────────────────────────
const eyebrowStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  textTransform: 'uppercase',
  letterSpacing: '0.38em',
  fontSize: 'clamp(10.5px, 0.82vw, 12.5px)',
  fontWeight: 500,
  color: CREAM_MID,
  display: 'flex',
  alignItems: 'center',
  gap: 14,
}

const eyebrowNumeral: React.CSSProperties = {
  fontFamily: FONT_SERIF,
  fontStyle: 'italic',
  fontSize: 'clamp(18px, 1.6vw, 26px)',
  letterSpacing: '-0.02em',
  color: CREAM_HIGH,
  textTransform: 'none',
  lineHeight: 1,
}

const eyebrowRule: React.CSSProperties = {
  flex: '0 0 56px',
  height: 1,
  background: 'rgba(244,236,211,0.38)',
  display: 'inline-block',
}

function Eyebrow({ num, label }: { num: string; label: string }) {
  return (
    <div style={eyebrowStyle}>
      <span style={eyebrowNumeral}>{num}</span>
      <span aria-hidden="true" style={eyebrowRule} />
      <span>{label}</span>
    </div>
  )
}

const sourceStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: 'clamp(9.5px, 0.72vw, 11px)',
  letterSpacing: '0.34em',
  textTransform: 'uppercase',
  fontWeight: 500,
  color: CREAM_FAINT,
}

const supportingStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: 'clamp(16px, 1.12vw, 19px)',
  lineHeight: 1.55,
  color: CREAM_MID,
  fontWeight: 300,
  maxWidth: '48ch',
}

const bigNumeralStyle: React.CSSProperties = {
  fontFamily: FONT_SERIF,
  fontWeight: 400,
  lineHeight: 0.86,
  letterSpacing: '-0.04em',
  color: CREAM,
  display: 'block',
  fontSize: 'clamp(140px, 20vw, 360px)',
  textShadow: '0 1px 0 rgba(0,0,0,0.15)',
}

const numeralCaption: React.CSSProperties = {
  fontFamily: FONT_DISP,
  textTransform: 'uppercase',
  letterSpacing: '0.34em',
  fontSize: 'clamp(11px, 0.88vw, 13px)',
  fontWeight: 600,
  color: CREAM_MID,
}

const stageStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'clamp(56px, 8vw, 120px)',
  pointerEvents: 'none',
}

const stageInner: React.CSSProperties = {
  width: '100%',
  maxWidth: 1280,
  margin: '0 auto',
}

// ─── Stages ───────────────────────────────────────────────────────────────────

function StageIntro() {
  const t = useTranslations('problem')
  return (
    <div data-stage="0" style={stageStyle}>
      <div style={stageInner}>
        <div data-anim style={{ marginBottom: 40 }}>
          <Eyebrow num="01" label={t('chapterLabel')} />
        </div>
        <div data-anim style={{ marginBottom: 28, ...sourceStyle, color: CREAM_LOW }}>
          {t('chapterSub')}
        </div>
        <h2
          data-anim="split"
          style={{
            fontFamily: FONT_DISP,
            fontWeight: 400,
            fontSize: 'clamp(58px, 7.2vw, 140px)',
            lineHeight: 0.96,
            letterSpacing: '-0.03em',
            color: CREAM,
            margin: '0 0 40px',
            maxWidth: '16ch',
          }}
        >
          {t('headline')}{' '}
          <em style={{
            fontFamily: FONT_SERIF,
            fontStyle: 'italic',
            fontWeight: 400,
            letterSpacing: '-0.02em',
            color: LIME,
          }}>{t('headlineItalic')}</em>
        </h2>
        <p data-anim style={{ ...supportingStyle, maxWidth: '58ch' }}>
          {t('intro')}
        </p>
      </div>
    </div>
  )
}

function StageBeat01() {
  const t = useTranslations('problem')
  return (
    <div data-stage="1" style={stageStyle}>
      <div style={{
        ...stageInner,
        display: 'grid',
        gridTemplateColumns: '5fr 7fr',
        gap: 'clamp(40px, 6vw, 96px)',
        alignItems: 'center',
      }}>
        <div>
          <div data-anim style={{ marginBottom: 32 }}>
            <Eyebrow num="01" label={t('beat01Label')} />
          </div>
          <p data-anim style={{ ...supportingStyle, marginBottom: 48, maxWidth: '38ch' }}>
            {t('beat01Copy')}
          </p>
          <div data-anim style={sourceStyle}>
            {t('beat01Source')}
          </div>
        </div>
        <div data-anim style={{ textAlign: 'right' }}>
          <span style={bigNumeralStyle}>
            <span data-countup="12">12</span>
            <span style={{
              fontFamily: FONT_SERIF,
              fontStyle: 'italic',
              fontSize: '0.30em',
              verticalAlign: 'super',
              color: CREAM_MID,
              marginLeft: '0.22em',
              letterSpacing: '-0.01em',
            }}>{t('beat01MillionLabel')}</span>
          </span>
          <div style={{ ...numeralCaption, marginTop: 20, textAlign: 'right' }}>
            {t('beat01Caption')}
          </div>
        </div>
      </div>
    </div>
  )
}

function StageBeat02() {
  const t = useTranslations('problem')
  return (
    <div data-stage="2" style={stageStyle}>
      <div style={{ ...stageInner, textAlign: 'center' }}>
        <div data-anim style={{ display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
          <Eyebrow num="02" label={t('beat02Label')} />
        </div>
        <div data-anim>
          <span style={{ ...bigNumeralStyle, fontSize: 'clamp(200px, 28vw, 540px)' }}>
            <span data-countup="90">90</span>
            <span style={{
              fontFamily: FONT_SERIF,
              fontStyle: 'italic',
              color: CREAM_MID,
            }}>%</span>
          </span>
        </div>
        <div data-anim style={{ ...numeralCaption, marginTop: 32 }}>
          {t('beat02Caption')}
        </div>
        <p data-anim style={{
          ...supportingStyle,
          maxWidth: '52ch',
          margin: '64px auto 0',
          textAlign: 'center',
        }}>
          {t('beat02Copy')}
        </p>
        <div data-anim style={{ ...sourceStyle, marginTop: 32 }}>
          {t('beat02Source')}
        </div>
      </div>
    </div>
  )
}

function StageBeat03() {
  const t = useTranslations('problem')
  return (
    <div data-stage="3" style={stageStyle}>
      <div style={stageInner}>
        <div data-anim style={{ marginBottom: 28 }}>
          <Eyebrow num="03" label={t('beat03Label')} />
        </div>
        <div data-anim style={{
          ...sourceStyle,
          color: CREAM_LOW,
          marginBottom: 56,
          display: 'flex',
          gap: 18,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <span>{t('beat03Loc')}</span>
          <span aria-hidden="true" style={{
            width: 6, height: 6, borderRadius: '50%',
            background: CREAM_LOW,
          }} />
          <span>{t('beat03Date')}</span>
        </div>

        <h3
          data-anim="split"
          style={{
            fontFamily: FONT_SERIF,
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: 'clamp(48px, 5.4vw, 96px)',
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            color: CREAM,
            margin: '0 0 72px',
            maxWidth: '22ch',
          }}
        >
          <span style={{ color: LIME, fontStyle: 'normal', opacity: 0.9 }}>“</span>
          {t('beat03Quote')}
          <span style={{ color: LIME, fontStyle: 'normal', opacity: 0.9 }}>”</span>
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 'clamp(28px, 4vw, 72px)',
          paddingTop: 36,
          borderTop: '1px solid rgba(244,236,211,0.14)',
        }}>
          <div data-anim>
            <div style={{
              fontFamily: FONT_SERIF,
              fontSize: 'clamp(48px, 4.8vw, 80px)',
              lineHeight: 1,
              letterSpacing: '-0.02em',
              color: CREAM,
            }}>
              <span data-countup="491">491</span>
              <span style={{ color: CREAM_MID, marginLeft: 4, fontSize: '0.55em' }}>mm</span>
            </div>
            <div style={{ ...numeralCaption, marginTop: 14, color: CREAM_LOW }}>
              {t('beat03RainCaption')}
            </div>
          </div>
          <div data-anim>
            <p style={{ ...supportingStyle, margin: 0, maxWidth: '34ch', fontSize: 'clamp(14px, 1vw, 16px)' }}>
              {t('beat03Copy')}
            </p>
            <div style={{ ...sourceStyle, marginTop: 18 }}>
              {t('beat03Source')}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StageBeat04() {
  const t = useTranslations('problem')
  return (
    <div data-stage="4" style={stageStyle}>
      <div style={{
        ...stageInner,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'clamp(48px, 8vw, 120px)',
        alignItems: 'center',
      }}>
        <div>
          <div data-anim style={{ marginBottom: 36 }}>
            <Eyebrow num="04" label={t('beat04Label')} />
          </div>
          <h3
            data-anim="split"
            style={{
              fontFamily: FONT_DISP,
              fontWeight: 400,
              fontSize: 'clamp(44px, 4.4vw, 84px)',
              lineHeight: 1.04,
              letterSpacing: '-0.02em',
              color: CREAM,
              margin: 0,
              maxWidth: '14ch',
            }}
          >
            {t('beat04HeadlinePre')}{' '}
            <em style={{
              fontFamily: FONT_SERIF,
              fontStyle: 'italic',
              fontWeight: 400,
              color: LIME,
              paddingRight: '0.06em',
            }}>{t('beat04HeadlineItalic')}</em>{' '}
            {t('beat04HeadlinePost')}
          </h3>
        </div>
        <div>
          <p data-anim style={{ ...supportingStyle, maxWidth: '38ch' }}>
            {t('beat04Copy')}
          </p>
          <div data-anim style={{ ...sourceStyle, marginTop: 36 }}>
            {t('beat04Source')}
          </div>
        </div>
      </div>
    </div>
  )
}


// ─── Section ──────────────────────────────────────────────────────────────────
export default function Problem() {
  const sectionRef = useRef<HTMLElement>(null)
  useProblemScene(sectionRef)

  return (
    <section
      ref={sectionRef}
      id="problem"
      style={{
        position: 'relative',
        color: CREAM,
        fontFamily: FONT_BODY,
        background: 'radial-gradient(1400px 900px at 78% 32%, rgba(122,74,30,0.22) 0%, rgba(122,74,30,0) 58%)',
      }}
    >
      {/* Hide all [data-anim] targets during SSR / pre-hydration so the
          italic accent word ("muriendo" / "dying") can't flash in before
          GSAP's initial autoAlpha:0 state is applied. Reduced-motion users
          get the content immediately. */}
      <style>{`
        #problem [data-anim] { opacity: 0; }
        @media (prefers-reduced-motion: reduce) {
          #problem [data-anim] { opacity: 1; }
        }

        /* ── Mobile: cinematic pin still plays, but content needs to fit
           inside 100dvh and stack instead of using grid columns. ───────── */
        @media (max-width: 768px) {
          #problem [data-stage] {
            padding: 72px 24px !important;
            align-items: center !important;
            justify-content: center !important;
          }
          #problem [data-stage] > div {
            width: 100%;
          }

          /* Stage 1 (12 million) — text + numeral side-by-side → stacked */
          #problem [data-stage="1"] > div {
            display: flex !important;
            flex-direction: column !important;
            gap: 32px !important;
          }
          #problem [data-stage="1"] [data-anim]:last-child {
            text-align: left !important;
          }
          #problem [data-stage="1"] [data-anim]:last-child > div:last-child {
            text-align: left !important;
          }

          /* Stage 3 (DANA case) — bottom stat grid → single stacked column */
          #problem [data-stage="3"] [style*="repeat(2"] {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
            padding-top: 24px !important;
          }
          /* Pull quote (italic block-quote) — bigger on mobile so it
             reads as the focal moment of the slide. */
          #problem [data-stage="3"] h3 {
            font-size: clamp(32px, 8.4vw, 52px) !important;
            line-height: 1.12 !important;
            margin-bottom: 32px !important;
          }

          /* Stage 4 (close) — 2-col headline + body → stacked */
          #problem [data-stage="4"] > div {
            display: flex !important;
            flex-direction: column !important;
            gap: 28px !important;
          }

          /* Headlines + numerals: tighten the giant clamps that look fine on
             desktop but blow past the viewport on a phone. */
          #problem [data-stage="0"] h2 {
            font-size: clamp(36px, 9.6vw, 56px) !important;
            margin-bottom: 24px !important;
          }
          #problem [data-stage="2"] [style*="28vw"] {
            font-size: clamp(120px, 36vw, 200px) !important;
          }
          #problem [data-stage="1"] [style*="20vw"] {
            font-size: clamp(72px, 22vw, 120px) !important;
          }
          /* "Devastating." and "491mm" stat headlines — shrink on mobile
             so they don't dominate the pull quote above. */
          #problem [data-stage="3"] [style*="4.8vw"] {
            font-size: clamp(48px, 13vw, 80px) !important;
          }
          #problem [data-stage="4"] h3 {
            font-size: clamp(30px, 8vw, 44px) !important;
          }

          /* Body copy + sources scale down proportionally */
          #problem [data-stage] p[style*="supportingStyle"],
          #problem p {
            font-size: 14px !important;
            line-height: 1.55 !important;
          }
        }
      `}</style>

      {/* Grain — fades in so it doesn't create a seam at the hero boundary */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(/grain.svg)',
          backgroundSize: '200px 200px',
          opacity: 0.10,
          mixBlendMode: 'screen',
          zIndex: 60,
          pointerEvents: 'none',
          maskImage: 'linear-gradient(to bottom, transparent 0px, black 200px)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0px, black 200px)',
        }}
      />

      {/* Viewport pinned by GSAP ScrollTrigger. The pin-spacer ScrollTrigger
          injects (sibling to this div) provides the scroll distance. */}
      <div
        data-viewport
        style={{
          position: 'relative',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Darkening overlay — scrubbed by useProblemScene. */}
        <div
          data-darken
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(10,6,3,0) 0%, rgba(10,6,3,0.55) 55%, rgba(6,3,2,0.95) 100%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* Stages — stacked, faded in/out by master timeline. */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 5 }}>
          <StageIntro />
          <StageBeat01 />
          <StageBeat02 />
          <StageBeat03 />
          <StageBeat04 />
        </div>

      </div>
    </section>
  )
}
